import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Check, Play, Sparkles, Tv, TrendingUp, Crown, Rocket, Zap, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TIER_ICONS = {
  starter: Play,
  growth: TrendingUp,
  premium: Crown,
  season: Tv,
  launch: Rocket,
};

function RequestModal({ pkg, onClose, onSuccess }) {
  const [form, setForm] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    vat_number: "", website: "", product_category: "", message: "", consent: false,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Consenso privacy richiesto"); return; }
    setLoading(true);
    try {
      await api.post("/media/packages/request", { ...form, package_id: pkg.id });
      toast.success(`Richiesta inviata · Ti ricontatteremo entro 24h`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="media-request-modal" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[hsl(var(--background))] max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#C9A961]/40 rounded-sm">
        <div className="p-6 md:p-8 border-b border-[hsl(var(--border))] flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] mb-2">Richiedi · {pkg.tier}</div>
            <h2 className="editorial-heading text-2xl md:text-3xl">{pkg.name}</h2>
            <div className="mt-2 text-[#C9A961] font-bold text-lg">{pkg.price_label}</div>
          </div>
          <button data-testid="media-modal-close" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={submit} className="p-6 md:p-8 space-y-4" data-testid="media-request-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider">Azienda *</label>
              <Input data-testid="mr-company" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider">Referente *</label>
              <Input data-testid="mr-contact" required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider">Email *</label>
              <Input data-testid="mr-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider">Telefono</label>
              <Input data-testid="mr-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider">P.IVA</label>
              <Input data-testid="mr-vat" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider">Sito web</label>
              <Input data-testid="mr-website" placeholder="https://…" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider">Categoria prodotto</label>
              <Select value={form.product_category || "_"} onValueChange={(v) => setForm({ ...form, product_category: v === "_" ? "" : v })}>
                <SelectTrigger data-testid="mr-category" className="mt-1"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_">—</SelectItem>
                  <SelectItem value="vino">Vino</SelectItem>
                  <SelectItem value="olio">Olio EVO</SelectItem>
                  <SelectItem value="salumi">Salumi & Formaggi</SelectItem>
                  <SelectItem value="pasta">Pasta & Conserve</SelectItem>
                  <SelectItem value="dolci">Dolci & Pasticceria</SelectItem>
                  <SelectItem value="ristorazione">Ristorazione / HoReCa</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider">Messaggio / Brief</label>
            <Textarea data-testid="mr-message" rows={3} placeholder="Raccontaci brevemente il progetto, obiettivi e tempistiche..."
              value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1" />
          </div>
          <label className="flex items-start gap-3 text-xs text-muted-foreground cursor-pointer">
            <input data-testid="mr-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1" />
            <span>Accetto la <a href="/privacy" target="_blank" rel="noreferrer" className="underline">privacy policy</a> e autorizzo Amazonico 3000 a contattarmi per questa richiesta commerciale.</span>
          </label>
          <div className="flex gap-3 pt-2">
            <Button data-testid="mr-submit" type="submit" disabled={loading} className="bg-[#7A1C2B] hover:bg-[#C9A961] hover:text-black text-white uppercase tracking-wider text-xs font-semibold flex-1">
              {loading ? "Invio..." : "Invia richiesta"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PackageCard({ pkg, onRequest }) {
  const Icon = TIER_ICONS[pkg.id] || Zap;
  return (
    <div data-testid={`media-pkg-${pkg.id}`} className="relative bg-[#0a0a0a] border border-white/10 hover:border-[#C9A961]/60 transition-colors p-7 flex flex-col rounded-sm">
      {pkg.popular && (
        <div className="absolute -top-3 left-7 px-3 py-1 bg-[#C9A961] text-black text-[10px] uppercase tracking-[0.25em] font-bold">Più richiesto</div>
      )}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#C9A961]" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">{pkg.tier}</div>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-white/40">{pkg.video_minutes} min</div>
      </div>
      <h3 className="editorial-heading text-2xl md:text-3xl text-white leading-tight">{pkg.name}</h3>
      <p className="mt-2 text-sm text-white/60">{pkg.tagline}</p>
      <div className="mt-5 pb-5 border-b border-white/10">
        <div className="text-2xl md:text-3xl font-bold text-[#C9A961]">{pkg.price_label}</div>
      </div>

      <ul className="mt-5 space-y-2.5 flex-1">
        {pkg.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/80">
            <Check className="h-4 w-4 text-[#C9A961] shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 pt-5 border-t border-white/10 space-y-3">
        <div>
          <div className="text-[9px] uppercase tracking-[0.3em] text-white/40 mb-2">KPI consegnati</div>
          <div className="flex flex-wrap gap-1.5">
            {pkg.kpis.map((k) => (
              <span key={k} className="text-[10px] px-2 py-0.5 border border-white/20 text-white/70 rounded-sm">{k}</span>
            ))}
          </div>
        </div>
      </div>

      {pkg.case_study && (
        <div data-testid={`media-case-${pkg.id}`} className="mt-5 p-4 bg-[#C9A961]/8 border-l-2 border-[#C9A961]">
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#C9A961] mb-2 flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 bg-[#C9A961] rounded-full animate-pulse" />
            Case study verificato
          </div>
          <div className="text-sm font-bold text-white">{pkg.case_study.brand}</div>
          <div className="text-xs text-[#C9A961] mt-1 font-semibold">{pkg.case_study.result}</div>
          <blockquote className="mt-2 text-xs text-white/60 italic leading-relaxed">"{pkg.case_study.quote}"</blockquote>
        </div>
      )}

      <Button
        data-testid={`media-pkg-cta-${pkg.id}`}
        onClick={() => onRequest(pkg)}
        className="mt-6 bg-[#7A1C2B] hover:bg-[#C9A961] hover:text-black text-white uppercase tracking-[0.2em] text-xs font-bold py-6"
      >
        Richiedi il pacchetto
      </Button>
    </div>
  );
}

export default function MediaKit() {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/media/packages").then((r) => setPackages(r.data.packages || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="media-kit-page" className="bg-[#050505] text-white min-h-screen">
      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #C9A961 0%, transparent 40%), radial-gradient(circle at 85% 70%, #7A1C2B 0%, transparent 50%)" }} />
        <div className="relative container-xl py-16 md:py-24">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.4em] text-[#C9A961] mb-5">
            <Sparkles className="h-3.5 w-3.5" />
            Amazonico TV · Media Kit 2026
          </div>
          <h1 className="editorial-heading text-5xl md:text-7xl leading-[0.95] max-w-4xl">
            Porta il tuo brand <br /><span className="italic text-[#C9A961]">nelle cantine d'Italia</span>
          </h1>
          <p className="mt-6 text-white/70 max-w-2xl text-lg">
            Cinque pacchetti editoriali su YouTube e Amazonico TV, integrati con il commerce: video, storytelling, product placement e tracking vendite. Raccontiamo la tua storia a un pubblico qualificato di wine lover, HoReCa e importatori.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#packages" className="px-6 py-3 bg-[#C9A961] hover:bg-white text-black uppercase tracking-[0.2em] text-xs font-bold transition-colors">Vedi pacchetti</a>
            <a href="/tv" className="px-6 py-3 bg-white/5 border border-white/20 hover:border-[#C9A961] uppercase tracking-[0.2em] text-xs font-bold transition-colors">Guarda Amazonico TV</a>
          </div>
        </div>
      </section>

      {/* PACKAGES GRID */}
      <section id="packages" className="container-xl py-16 md:py-20">
        <div className="mb-12">
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#C9A961] mb-3">I pacchetti</div>
          <h2 className="editorial-heading text-4xl md:text-5xl">Cinque formati per ogni obiettivo.</h2>
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
          {packages.map((p) => <PackageCard key={p.id} pkg={p} onRequest={setSelected} />)}
        </div>
      </section>

      {/* SOCIAL PROOF — KPI globali + brand wall */}
      <section data-testid="media-social-proof" className="border-t border-white/10 bg-gradient-to-b from-[#0a0a0a] to-[#050505]">
        <div className="container-xl py-14 md:py-20">
          <div className="text-center mb-10">
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#C9A961] mb-3">Hanno scelto Amazonico TV</div>
            <h2 className="editorial-heading text-3xl md:text-4xl">12 brand · 1,4M view · €890K revenue tracciati</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 max-w-4xl mx-auto">
            {[
              { v: "1.4M+", l: "View totali" },
              { v: "12.8%", l: "CTR medio" },
              { v: "€890K", l: "Sales tracked" },
              { v: "94%", l: "Brand satisfaction" },
            ].map((s) => (
              <div key={s.l} className="text-center p-5 border border-white/10 rounded-sm bg-white/[0.02]">
                <div className="text-3xl md:text-4xl font-bold text-[#C9A961] tabular-nums">{s.v}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mt-2">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { brand: "Cantina Pietra Bianca", role: "Piccolo produttore · Toscana", quote: "Da brand sconosciuto a 4 importatori in 60 giorni. Il pacchetto Starter è stato il miglior investimento del 2025.", author: "Elena F. · Founder" },
              { brand: "Tenuta Le Querce", role: "Riserva premium · Veneto", quote: "Il badge \"Visto su Amazonico TV\" sulla scheda prodotto ha cambiato il sell-out della Riserva: +47% in 90 giorni.", author: "Marco V. · CEO" },
              { brand: "Consorzio Brunello", role: "Consorzio · Montalcino", quote: "186K view con un'audience di buyer professionali in 14 paesi. Posizionamento internazionale a un costo imbattibile.", author: "Direzione consorzio" },
            ].map((t) => (
              <div key={t.brand} className="p-6 bg-[#0a0a0a] border border-white/10 rounded-sm">
                <div className="text-[#C9A961] text-2xl leading-none mb-3">"</div>
                <p className="text-sm text-white/80 leading-relaxed italic">{t.quote}</p>
                <div className="mt-5 pt-4 border-t border-white/10">
                  <div className="font-semibold text-white text-sm">{t.brand}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#C9A961] mt-0.5">{t.role}</div>
                  <div className="text-xs text-white/50 mt-1">{t.author}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="border-t border-white/10 bg-[#0a0a0a]">
        <div className="container-xl py-14 text-center">
          <h3 className="editorial-heading text-3xl md:text-4xl">Hai in mente qualcosa di diverso?</h3>
          <p className="mt-3 text-white/60">Progettiamo format custom per consorzi, GDO, partnership regionali.</p>
          <Button
            data-testid="media-kit-custom-cta"
            onClick={() => setSelected({ id: "custom", name: "Progetto Custom", tier: "Custom", price_label: "A preventivo", features: [], kpis: [] })}
            className="mt-6 bg-[#7A1C2B] hover:bg-[#C9A961] hover:text-black text-white uppercase tracking-[0.2em] text-xs font-bold px-8 py-6"
          >
            Parliamone
          </Button>
        </div>
      </section>

      {selected && <RequestModal pkg={selected} onClose={() => setSelected(null)} onSuccess={() => setSelected(null)} />}
    </div>
  );
}
